import { useCallback } from 'react';
import { 
  convertAIContentToTipTap, 
  ensureTipTapFormat, 
  debugContentConversion,
  convertAIContentWithMetadata,
  isTipTapHTML,
  isMarkdown
} from '@/utils/ai-to-tiptap-converter';

/**
 * Hook for converting AI-generated content to TipTap format
 * 
 * Provides utilities for:
 * - Converting markdown to TipTap HTML
 * - Detecting content format
 * - Debugging conversion issues
 * - Batch processing multiple content items
 */
export function useContentConverter() {
  /**
   * Convert AI-generated markdown content to TipTap HTML
   */
  const convertToTipTap = useCallback((content: string) => {
    return convertAIContentToTipTap(content);
  }, []);

  /**
   * Smart converter that detects format and converts if needed
   */
  const ensureCompatible = useCallback((content: string) => {
    return ensureTipTapFormat(content);
  }, []);

  /**
   * Convert with detailed metadata about the conversion
   */
  const convertWithMetadata = useCallback((content: string) => {
    return convertAIContentWithMetadata(content);
  }, []);

  /**
   * Debug conversion process
   */
  const debugConversion = useCallback((content: string) => {
    return debugContentConversion(content);
  }, []);

  /**
   * Check if content is already TipTap HTML
   */
  const isCompatible = useCallback((content: string) => {
    return isTipTapHTML(content);
  }, []);

  /**
   * Check if content appears to be markdown
   */
  const isMarkdownContent = useCallback((content: string) => {
    return isMarkdown(content);
  }, []);

  /**
   * Batch convert multiple content items
   */
  const convertMultiple = useCallback((contents: string[]) => {
    return contents.map(content => ensureTipTapFormat(content));
  }, []);

  /**
   * Get content format information
   */
  const getContentInfo = useCallback((content: string) => {
    const debug = debugContentConversion(content);
    return {
      format: debug.originalType,
      length: debug.originalLength,
      needsConversion: debug.wasConverted,
      preview: debug.preview
    };
  }, []);

  return {
    // Core conversion functions
    convertToTipTap,
    ensureCompatible,
    convertWithMetadata,
    
    // Detection functions
    isCompatible,
    isMarkdownContent,
    getContentInfo,
    
    // Utility functions
    debugConversion,
    convertMultiple,
    
    // Convenience functions
    convertIfNeeded: ensureCompatible,
    checkFormat: getContentInfo,
  };
}

/**
 * Hook for migrating existing content in the database
 * 
 * Useful for one-time migrations or batch updates
 */
export function useContentMigration() {
  const { convertWithMetadata, debugConversion } = useContentConverter();

  /**
   * Migrate a single content item and return migration info
   */
  const migrateContent = useCallback((content: string) => {
    const result = convertWithMetadata(content);
    
    return {
      ...result,
      migrationNeeded: result.metadata.wasConverted,
      migrationInfo: {
        originalFormat: result.metadata.originalFormat,
        sizeChange: result.metadata.convertedLength - result.metadata.originalLength,
        successful: result.html.length > 0
      }
    };
  }, [convertWithMetadata]);

  /**
   * Analyze content without converting (for planning migrations)
   */
  const analyzeContent = useCallback((content: string) => {
    const debug = debugConversion(content);
    
    return {
      needsMigration: debug.originalType === 'markdown',
      currentFormat: debug.originalType,
      size: debug.originalLength,
      complexity: {
        hasHeaders: /^#{1,6}\s+/m.test(content),
        hasLists: /^[\*\-\+]\s+/m.test(content) || /^\d+\.\s+/m.test(content),
        hasLinks: /\[.*?\]\(.*?\)/.test(content),
        hasCode: /`.*?`/.test(content) || /^```/m.test(content),
        hasTables: /\|.*\|/.test(content),
      }
    };
  }, [debugConversion]);

  /**
   * Batch analyze multiple content items
   */
  const analyzeMultiple = useCallback((contents: string[]) => {
    const results = contents.map(analyzeContent);
    
    const summary = {
      total: contents.length,
      needsMigration: results.filter(r => r.needsMigration).length,
      formats: {
        markdown: results.filter(r => r.currentFormat === 'markdown').length,
        html: results.filter(r => r.currentFormat === 'html').length,
        plain: results.filter(r => r.currentFormat === 'plain').length,
        empty: results.filter(r => r.currentFormat === 'empty').length,
      },
      complexity: {
        withHeaders: results.filter(r => r.complexity.hasHeaders).length,
        withLists: results.filter(r => r.complexity.hasLists).length,
        withLinks: results.filter(r => r.complexity.hasLinks).length,
        withCode: results.filter(r => r.complexity.hasCode).length,
        withTables: results.filter(r => r.complexity.hasTables).length,
      }
    };

    return {
      results,
      summary
    };
  }, [analyzeContent]);

  return {
    migrateContent,
    analyzeContent,
    analyzeMultiple,
  };
} 